import { createContext, type PropsWithChildren, useCallback, useMemo } from 'react';
import { type StoreApi } from 'zustand';

import { useRegisterOcBetslipCallbacks } from '@cx-modules/hooks';

import { type InitialState, type OutrightsCouponState, type SyncBet } from '../interfaces/outright-markets.interface';
import { createOutrightsCouponStore } from '../OutrightsCoupon.store';

export const OutrightsCouponContext = createContext<StoreApi<OutrightsCouponState> | null>(null);

interface OutrightsCouponProviderProps extends PropsWithChildren, InitialState {}

export const OutrightsCouponProvider = ({ children, ...props }: OutrightsCouponProviderProps) => {
  const store = useMemo(() => createOutrightsCouponStore(props), [props]);

  const syncBetslip = useCallback(
    (bets: SyncBet[], overwriteSelections?: boolean) => {
      store.getState().syncBetslip?.(bets, overwriteSelections);
    },
    [store],
  );

  const removeAllSelectedBets = useCallback(() => {
    store.getState().removeAllSelectedBets?.();
  }, [store]);

  const removeSelectedBet = useCallback(
    (betIdOrObj: number | { betId: number }) => {
      const betId = typeof betIdOrObj === 'number' ? betIdOrObj : betIdOrObj.betId;
      store.getState().removeSelectedBet?.(betId);
    },
    [store],
  );

  useRegisterOcBetslipCallbacks(
    {
      clearBetslip: removeAllSelectedBets,
      removeAllBets: removeAllSelectedBets,
      removeBet: removeSelectedBet,
      syncBetslip,
    },
    [removeAllSelectedBets, removeSelectedBet, syncBetslip],
  );

  return <OutrightsCouponContext.Provider value={store}>{children}</OutrightsCouponContext.Provider>;
};
